import { Check, Monitor, Moon, Sun } from "lucide-react";
import React from "react";
import {
	DARK_THEMES,
	LIGHT_THEMES,
	THEMES,
	THEME_MODES,
	useTheme,
} from "../ThemeContext";
import { DropdownMenu } from "./DropdownMenu";

const modeItems = [
	{ mode: THEME_MODES.LIGHT, label: "Light", icon: Sun },
	{ mode: THEME_MODES.DARK, label: "Dark", icon: Moon },
	{ mode: THEME_MODES.SYSTEM, label: "System", icon: Monitor },
];

const themeNames = {
	[THEMES.LIGHT]: "Light",
	[THEMES.DARK]: "Dark",
	[THEMES.MONOKAI]: "Monokai",
	[THEMES.DRACULA]: "Dracula",
	[THEMES.NORD]: "Nord",
	[THEMES.SOLARIZED]: "Solarized",
	[THEMES.GITHUB]: "GitHub",
	[THEMES.NIGHTOWL]: "Night Owl",
	[THEMES.SYNTHWAVE]: "Synthwave",
	[THEMES.AURA]: "Aura",
};

const themePreviewColors = {
	[THEMES.LIGHT]: ["#ffffff", "#f3f4f6", "#3b82f6"],
	[THEMES.DARK]: ["#1f2937", "#111827", "#3b82f6"],
	[THEMES.MONOKAI]: ["#272822", "#49483E", "#66D9EF"],
	[THEMES.DRACULA]: ["#282a36", "#44475a", "#bd93f9"],
	[THEMES.NORD]: ["#2e3440", "#434c5e", "#88c0d0"],
	[THEMES.SOLARIZED]: ["#fdf6e3", "#eee8d5", "#268bd2"],
	[THEMES.GITHUB]: ["#ffffff", "#f1f8ff", "#005cc5"],
	[THEMES.NIGHTOWL]: ["#011627", "#1d3b53", "#82aaff"],
	[THEMES.SYNTHWAVE]: ["#2d2b55", "#34294f", "#ff7edb"],
	[THEMES.AURA]: ["#15141b", "#2d2b38", "#a277ff"],
};

const ThemePreview = ({ palette }) => {
	const colors = themePreviewColors[palette] || themePreviewColors[THEMES.DARK];
	return (
		<span className="flex h-6 w-9 shrink-0 overflow-hidden rounded-md shadow-sm ring-1 ring-black/10 dark:ring-white/15">
			<span className="w-2/3" style={{ backgroundColor: colors[0] }} />
			<span className="w-1/3" style={{ backgroundColor: colors[1] }} />
			<span className="w-1/12" style={{ backgroundColor: colors[2] }} />
		</span>
	);
};

const ActiveIcon = ({ themeMode }) => {
	const Icon =
		modeItems.find((item) => item.mode === themeMode)?.icon || Monitor;
	return (
		<Icon className="h-3.5 w-3.5 transition-transform duration-500 ease-out motion-safe:group-hover:rotate-90" />
	);
};

export const ThemeMenu = () => {
	const {
		themeMode,
		setThemeMode,
		resolvedThemeMode,
		theme,
		setTheme,
		lightTheme,
		darkTheme,
	} = useTheme();

	const palettes =
		resolvedThemeMode === THEME_MODES.LIGHT ? LIGHT_THEMES : DARK_THEMES;
	const activePalette =
		resolvedThemeMode === THEME_MODES.LIGHT ? lightTheme : darkTheme;

	return (
		<DropdownMenu
			ariaLabel={`Theme (mode: ${themeMode}, palette: ${activePalette})`}
			title="Theme"
			panelClassName="w-60"
			buttonClassName="group flex h-8 w-8 items-center justify-center rounded-md text-[var(--surface-elevated-muted)] hover:bg-[var(--surface-elevated-hover)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-opacity-50 active:scale-[0.96] transition-[background-color,color,transform]"
			trigger={<ActiveIcon themeMode={themeMode} />}
		>
			<div className="px-2 pb-1 pt-1.5 text-xs font-semibold text-[var(--surface-elevated-muted)]">
				Mode
			</div>
			<div className="grid grid-cols-3 gap-1 px-1 pb-1.5">
				{modeItems.map(({ mode, label, icon: Icon }) => {
					const selected = themeMode === mode;
					return (
						<button
							key={mode}
							type="button"
							onClick={() => setThemeMode(mode)}
							role="menuitemradio"
							aria-checked={selected}
							className={`flex flex-col items-center gap-1 rounded-lg px-2 py-2 text-xs font-medium active:scale-[0.96] transition-[background-color,color,box-shadow,transform] ${
								selected
									? "bg-[var(--color-accent)] text-[var(--color-accent-contrast)] shadow-sm"
									: "text-[var(--surface-elevated-muted)] hover:bg-[var(--surface-elevated-hover)] hover:text-[var(--color-text-primary)]"
							}`}
						>
							<Icon className="h-4 w-4" />
							{label}
						</button>
					);
				})}
			</div>
			<div className="my-1 h-px bg-[var(--color-border)]" />
			<div className="px-2 pb-1 pt-0.5 text-xs font-semibold text-[var(--surface-elevated-muted)]">
				Palette
			</div>
			<div className="max-h-56 overflow-y-auto pb-0.5">
				{palettes.map((palette) => {
					const selected = activePalette === palette && theme === palette;
					return (
						<button
							key={palette}
							type="button"
							onClick={() => setTheme(palette)}
							role="menuitemradio"
							aria-checked={selected}
							className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm text-[var(--color-text-primary)] active:scale-[0.98] transition-[background-color,transform] ${
								selected
									? "bg-[var(--surface-elevated-hover)]"
									: "hover:bg-[var(--surface-elevated-hover)]"
							}`}
						>
							<ThemePreview palette={palette} />
							<span className="flex-1 text-left">{themeNames[palette]}</span>
							{selected && (
								<Check className="h-4 w-4 shrink-0 text-[var(--color-accent)]" />
							)}
						</button>
					);
				})}
			</div>
		</DropdownMenu>
	);
};
